import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Bookmark,
  BookmarkX,
  MapPin,
  Clock,
  Building2,
  DollarSign,
  Briefcase,
  ChevronRight,
  Loader2,
  ExternalLink,
  Trash2
} from "lucide-react";

const SavedJobs = () => {
  const navigate = useNavigate();
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingJob, setRemovingJob] = useState(null);

  useEffect(() => {
    fetchSavedJobs();
  }, []);

  const fetchSavedJobs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/jobs/saved/list`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedJobs(data);
      } else {
        toast.error("Failed to load saved jobs");
      }
    } catch (error) {
      console.error("Failed to fetch saved jobs:", error);
      toast.error("Failed to load saved jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleUnsaveJob = async (jobId, jobTitle) => {
    setRemovingJob(jobId);
    try {
      const response = await fetch(`${API}/jobs/${jobId}/save`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        setSavedJobs(prev => prev.filter(j => j.job_id !== jobId));
        toast.success(`Removed "${jobTitle}" from saved jobs`);
      } else {
        toast.error("Failed to remove job");
      }
    } catch (error) {
      toast.error("Failed to remove job");
    } finally {
      setRemovingJob(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatSalary = (min, max, currency = "USD") => {
    if (!min && !max) return null;
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    if (min && max) return `${formatter.format(min)} - ${formatter.format(max)}`;
    if (min) return `From ${formatter.format(min)}`;
    return `Up to ${formatter.format(max)}`;
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="saved-jobs-page">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-crimson/10 border border-crimson/30 mb-3">
                <Bookmark className="w-3 h-3 text-crimson" />
                <span className="text-xs uppercase tracking-widest text-crimson font-medium">Saved for Later</span>
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                SAVED <span className="text-crimson">JOBS</span>
              </h1>
              <p className="text-gray-500 text-sm mt-1">{savedJobs.length} job{savedJobs.length !== 1 ? 's' : ''} saved</p>
            </div>
            
            <Link to="/jobs">
              <Button className="bg-crimson hover:bg-crimson-hover">
                Browse More Jobs
              </Button>
            </Link>
          </div>

          {/* Jobs List */}
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-crimson" />
            </div>
          ) : savedJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-obsidian-light border border-[#222]">
              <BookmarkX className="w-16 h-16 text-gray-600 mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Saved Jobs Yet</h3>
              <p className="text-gray-500 text-sm max-w-sm mb-6">
                Save interesting jobs while browsing to apply later. Click the bookmark icon on any job to save it.
              </p>
              <Link to="/jobs">
                <Button className="bg-crimson hover:bg-crimson-hover">
                  Browse Jobs
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {savedJobs.map((job, index) => (
                <motion.div
                  key={job.job_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-obsidian-light border border-[#222] hover:border-[#333] transition-colors"
                  data-testid={`saved-job-${job.job_id}`}
                >
                  <div className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3 mb-2">
                          <div className="w-12 h-12 bg-gradient-to-br from-crimson/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                            <Briefcase className="w-6 h-6 text-crimson" />
                          </div>
                          <div className="min-w-0">
                            <Link 
                              to={`/jobs/${job.job_id}`}
                              className="font-heading text-lg font-bold text-white hover:text-crimson transition-colors block truncate"
                            >
                              {job.title}
                            </Link>
                            <p className="text-gray-400 text-sm">{job.company_name || "Company"}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-3 text-sm text-gray-500 mt-3">
                          {job.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                          )}
                          {job.employment_type && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {job.employment_type.replace("-", " ").replace(/\b\w/g, c => c.toUpperCase())}
                            </span>
                          )}
                          {job.industry && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {job.industry}
                            </span>
                          )}
                        </div>
                        
                        {(job.salary_min || job.salary_max) && (
                          <div className="flex items-center gap-1 text-green-500 text-sm mt-2">
                            <DollarSign className="w-4 h-4" />
                            {formatSalary(job.salary_min, job.salary_max, job.currency)}
                          </div>
                        )}
                        
                        {job.skills && job.skills.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {job.skills.slice(0, 5).map((skill, i) => (
                              <span 
                                key={i}
                                className="px-2 py-1 text-xs bg-obsidian border border-[#333] text-gray-400"
                              >
                                {skill}
                              </span>
                            ))}
                            {job.skills.length > 5 && (
                              <span className="px-2 py-1 text-xs text-gray-600">
                                +{job.skills.length - 5} more
                              </span>
                            )}
                          </div>
                        )}
                        
                        {job.saved_at && (
                          <p className="text-xs text-gray-600 mt-3">
                            Saved on {formatDate(job.saved_at)}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex sm:flex-col gap-2 sm:items-end">
                        <Link to={`/jobs/${job.job_id}`}>
                          <Button 
                            variant="outline" 
                            className="border-[#333] text-white hover:border-crimson"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          className="border-[#333] text-red-500 hover:border-red-500 hover:bg-red-500/10"
                          onClick={() => handleUnsaveJob(job.job_id, job.title)}
                          disabled={removingJob === job.job_id}
                          data-testid={`unsave-btn-${job.job_id}`}
                        >
                          {removingJob === job.job_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SavedJobs;
